import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import { useClientExportFlow } from '../../hooks/useClientExportFlow';
import './chartjs-register';
import { DASHBOARD_VIEWS, TYPE_OPTIONS, type DashboardViewId } from './types';
import { useKpiDashboardFilters } from './useKpiDashboardFilters';
import { useKpiDashboardCharts } from './useKpiDashboardCharts';
import KpiDashboardFilterBar from './KpiDashboardFilterBar';
import KpiDashboardContent from './KpiDashboardContent';
import KpiHeatmapCustomerTab from './KpiHeatmapCustomerTab';
import KpiHeatmapCiTab from './KpiHeatmapCiTab';
import KpiIncidentDashboardTab from './KpiIncidentDashboardTab';
import KpiServiceRequestDashboardTab from './KpiServiceRequestDashboardTab';
import KpiCaseDashboardTab from './KpiCaseDashboardTab';
import KpiChangeRequestDashboardTab from './KpiChangeRequestDashboardTab';
import KpiChangeDetailsTab from './KpiChangeDetailsTab';
import KpiAllTicketsTab from './KpiAllTicketsTab';
import KpiSlaViewTab from './KpiSlaViewTab';
import KpiProblemDashboardTab from './KpiProblemDashboardTab';
import KpiResilienceDashboardTab from './KpiResilienceDashboardTab';
import KpiMsrReportTab from './KpiMsrReportTab';
import KpiDrilldownTicketsPanel from './KpiDrilldownTicketsPanel';
import { useKpiHeatmapCustomerView } from './useKpiHeatmapCustomerView';
import { useKpiHeatmapCiView } from './useKpiHeatmapCiView';
import { useMsrReport, msrReportEnabled } from './useMsrReport';
import { useAzureCostSections } from './useAzureCost';
import type { KpiDrilldownSpec, KpiHeatmapCiFocus } from './kpiDashboardApi';
import type { KpiResilienceCrossSlice } from './kpiResilienceCrossFilter';
import { getKpiExportDateRange } from './kpiExportDateRange';
import {
  exportKpiCaseTabPdf,
  exportKpiChangeRequestTabPdf,
  exportKpiDashboardMainPdf,
  exportKpiFilterContextPdf,
  exportKpiHeatmapCiViewPdf,
  exportKpiHeatmapCustomerViewPdf,
  exportKpiIncidentTabPdf,
  exportKpiProblemsTabPdf,
  exportKpiResiliencePdf,
  exportKpiServiceRequestTabPdf,
  type KpiPdfExport,
} from './kpiDashboardPdfExport';
import { exportAgeingTableExcel, exportLastUpdatedTableExcel } from './KpiOperationalSummaryTables';
import { exportMsrReportPdf } from './msrReportPdfExport';
import { exportMsrRawDataExcel } from './msrReportRawExport';
import { fetchMsrReportRawData } from './kpiDashboardApi';

const ALL_TICKET_TYPE_VALUES = TYPE_OPTIONS.map((o) => o.value) as string[];

const KpiDashboard: React.FC = () => {
  const [activeViewId, setActiveViewId] = useState<DashboardViewId>('dashboard');
  const [drill, setDrill] = useState<KpiDrilldownSpec | null>(null);
  const [resilienceCrossFilter, setResilienceCrossFilter] = useState<KpiResilienceCrossSlice>(null);
  const [heatmapCiFocus, setHeatmapCiFocus] = useState<KpiHeatmapCiFocus>({
    resourceId: null,
    label: null,
    weekStart: null,
    assignmentGroupName: null,
    assigneeUserId: null,
  });
  const {
    filterKey,
    filterValues,
    filterSections,
    activeFilterChips,
    groups,
    assignmentGroupQuickValue,
    handleFilterChange,
    handleAssignmentGroupQuickChange,
    handleRemoveFilterChip,
    handleClearAllFilters,
  } = useKpiDashboardFilters();

  useEffect(() => {
    setResilienceCrossFilter(null);
  }, [filterKey]);

  useEffect(() => {
    setHeatmapCiFocus({
      resourceId: null,
      label: null,
      weekStart: null,
      assignmentGroupName: null,
      assigneeUserId: null,
    });
  }, [filterKey]);

  useEffect(() => {
    if (activeViewId !== 'heatmap-ci') {
      setHeatmapCiFocus({
        resourceId: null,
        label: null,
        weekStart: null,
        assignmentGroupName: null,
        assigneeUserId: null,
      });
    }
  }, [activeViewId]);

  useEffect(() => {
    if (activeViewId !== 'resilience') {
      setResilienceCrossFilter(null);
    }
  }, [activeViewId]);

  /** Incident tab scopes charts + heatmap to Incident ticket type (same pattern as Bandwidth dashboard layout). */
  const incidentScopedFilters = useMemo(() => {
    if (activeViewId !== 'incident') return filterValues;
    return { ...filterValues, ticketType: ['Incident'] as string[] };
  }, [activeViewId, filterValues]);

  const incidentScopedFilterKey = useMemo(() => {
    if (activeViewId !== 'incident') return filterKey;
    return `${filterKey}-view:incident`;
  }, [activeViewId, filterKey]);

  const requestScopedFilters = useMemo(() => {
    if (activeViewId !== 'request') return filterValues;
    return { ...filterValues, ticketType: ['Request'] as string[] };
  }, [activeViewId, filterValues]);

  const requestScopedFilterKey = useMemo(() => {
    if (activeViewId !== 'request') return filterKey;
    return `${filterKey}-view:request`;
  }, [activeViewId, filterKey]);

  const caseScopedFilters = useMemo(() => {
    if (activeViewId !== 'case') return filterValues;
    return { ...filterValues, ticketType: ['Case'] as string[] };
  }, [activeViewId, filterValues]);

  const caseScopedFilterKey = useMemo(() => {
    if (activeViewId !== 'case') return filterKey;
    return `${filterKey}-view:case`;
  }, [activeViewId, filterKey]);

  const changeScopedFilters = useMemo(() => {
    if (activeViewId !== 'change-request') return filterValues;
    return { ...filterValues, ticketType: ['Change'] as string[] };
  }, [activeViewId, filterValues]);

  const changeScopedFilterKey = useMemo(() => {
    if (activeViewId !== 'change-request') return filterKey;
    return `${filterKey}-view:change-request`;
  }, [activeViewId, filterKey]);

  const changeDetailsScopedFilters = useMemo(() => {
    if (activeViewId !== 'change-details') return filterValues;
    return { ...filterValues, ticketType: ['Change'] as string[] };
  }, [activeViewId, filterValues]);

  const changeDetailsScopedFilterKey = useMemo(() => {
    if (activeViewId !== 'change-details') return filterKey;
    return `${filterKey}-view:change-details`;
  }, [activeViewId, filterKey]);

  const allTicketsScopedFilters = useMemo(() => {
    if (activeViewId !== 'all-tickets') return filterValues;
    return { ...filterValues, ticketType: ALL_TICKET_TYPE_VALUES };
  }, [activeViewId, filterValues]);

  const allTicketsScopedFilterKey = useMemo(() => {
    if (activeViewId !== 'all-tickets') return filterKey;
    return `${filterKey}-view:all-tickets`;
  }, [activeViewId, filterKey]);

  const slaViewScopedFilters = useMemo(() => {
    if (activeViewId !== 'sla-view') return filterValues;
    // SLA View supports all ticket types; respect global ticketType filter when set.
    const tt = filterValues.ticketType;
    if (Array.isArray(tt) && tt.length > 0) return filterValues;
    return { ...filterValues, ticketType: ALL_TICKET_TYPE_VALUES };
  }, [activeViewId, filterValues]);

  const slaViewScopedFilterKey = useMemo(() => {
    if (activeViewId !== 'sla-view') return filterKey;
    return `${filterKey}-view:sla-view`;
  }, [activeViewId, filterKey]);

  const problemScopedFilters = useMemo(() => {
    if (activeViewId !== 'problems') return filterValues;
    return { ...filterValues, ticketType: ['Problem'] as string[] };
  }, [activeViewId, filterValues]);

  const problemScopedFilterKey = useMemo(() => {
    if (activeViewId !== 'problems') return filterKey;
    return `${filterKey}-view:problems`;
  }, [activeViewId, filterKey]);

  const chartsQuery = useKpiDashboardCharts(
    activeViewId === 'incident'
      ? incidentScopedFilterKey
      : activeViewId === 'request'
        ? requestScopedFilterKey
        : activeViewId === 'case'
          ? caseScopedFilterKey
          : activeViewId === 'change-request'
            ? changeScopedFilterKey
            : activeViewId === 'problems'
              ? problemScopedFilterKey
              : filterKey,
    activeViewId === 'incident'
      ? incidentScopedFilters
      : activeViewId === 'request'
        ? requestScopedFilters
        : activeViewId === 'case'
          ? caseScopedFilters
          : activeViewId === 'change-request'
            ? changeScopedFilters
            : activeViewId === 'problems'
              ? problemScopedFilters
              : filterValues,
    {
      enabled:
        activeViewId === 'dashboard' ||
        activeViewId === 'incident' ||
        activeViewId === 'request' ||
        activeViewId === 'case' ||
        activeViewId === 'change-request' ||
        activeViewId === 'problems' ||
        activeViewId === 'resilience',
      resilienceCrossFilter: activeViewId === 'resilience' ? resilienceCrossFilter : null,
    }
  );

  const heatmapCiQuery = useKpiHeatmapCiView(filterKey, filterValues, heatmapCiFocus, {
    enabled: activeViewId === 'heatmap-ci',
  });

  const heatmapQuery = useKpiHeatmapCustomerView(
    activeViewId === 'incident'
      ? incidentScopedFilterKey
      : activeViewId === 'request'
        ? requestScopedFilterKey
        : filterKey,
    activeViewId === 'incident'
      ? incidentScopedFilters
      : activeViewId === 'request'
        ? requestScopedFilters
        : filterValues,
    {
      enabled:
        activeViewId === 'heatmap-customer' ||
        activeViewId === 'incident' ||
        activeViewId === 'request',
    }
  );

  const msrReportQuery = useMsrReport(filterKey, filterValues, {
    enabled: activeViewId === 'msr-report',
  });

  const msrCustomerSelected = msrReportEnabled(filterValues);

  const selectedCustomerName = useMemo(() => {
    const chip = activeFilterChips.find((c) => c.id === 'customer');
    return chip?.value || 'Customer';
  }, [activeFilterChips]);

  useEffect(() => {
    if (
      activeViewId !== 'dashboard' &&
      activeViewId !== 'heatmap-customer' &&
      activeViewId !== 'heatmap-ci' &&
      activeViewId !== 'incident' &&
      activeViewId !== 'request' &&
      activeViewId !== 'case' &&
      activeViewId !== 'change-request' &&
      activeViewId !== 'problems' &&
      activeViewId !== 'resilience'
    ) {
      setDrill(null);
    }
  }, [activeViewId]);

  const chartsError =
    chartsQuery.error instanceof Error
      ? chartsQuery.error
      : chartsQuery.error
        ? new Error(String(chartsQuery.error))
        : null;

  const heatmapError =
    heatmapQuery.error instanceof Error
      ? heatmapQuery.error
      : heatmapQuery.error
        ? new Error(String(heatmapQuery.error))
        : null;

  const heatmapCiError =
    heatmapCiQuery.error instanceof Error
      ? heatmapCiQuery.error
      : heatmapCiQuery.error
        ? new Error(String(heatmapCiQuery.error))
        : null;

  const msrReportError =
    msrReportQuery.error instanceof Error
      ? msrReportQuery.error
      : msrReportQuery.error
        ? new Error(String(msrReportQuery.error))
        : null;

  const activeViewLabel = DASHBOARD_VIEWS.find((v) => v.id === activeViewId)?.label ?? 'Dashboard';

  const { dateFrom, dateTo } = useMemo(() => getKpiExportDateRange(filterValues), [filterValues]);

  const azureCostSections = useAzureCostSections(filterKey, filterValues, dateTo, {
    enabled: activeViewId === 'msr-report',
  });

  const toQueryError = (err: unknown): Error | null => {
    if (!err) return null;
    return err instanceof Error ? err : new Error(String(err));
  };

  const { run, modal } = useClientExportFlow();

  const exportPdfDisabled = useMemo(() => {
    if (drill !== null) return true;
    switch (activeViewId) {
      case 'dashboard':
      case 'case':
      case 'change-request':
      case 'problems':
      case 'resilience':
        return !chartsQuery.data;
      case 'heatmap-customer':
        return !heatmapQuery.data;
      case 'heatmap-ci':
        return !heatmapCiQuery.data;
      case 'incident':
      case 'request':
        return !chartsQuery.data || !heatmapQuery.data;
      case 'change-details':
      case 'all-tickets':
      case 'sla-view':
        return false;
      case 'msr-report':
        return !msrCustomerSelected || !msrReportQuery.data;
      default:
        return true;
    }
  }, [drill, activeViewId, chartsQuery.data, heatmapQuery.data, heatmapCiQuery.data, msrCustomerSelected, msrReportQuery.data]);

  const exportShowExcel = activeViewId === 'dashboard';
  const exportShowCsv = activeViewId === 'msr-report';

  const exportExcelDisabled = useMemo(() => {
    if (activeViewId === 'msr-report') {
      return !msrCustomerSelected;
    }
    if (activeViewId === 'dashboard') {
      return !chartsQuery.data;
    }
    return true;
  }, [activeViewId, msrCustomerSelected, chartsQuery.data]);

  const handleExportPdf = useCallback(() => {
    void run({
      downloadButtonLabel: 'Download PDF',
      work: async () => {
        if (drill !== null) {
          throw new Error('Close the drill-down panel before exporting.');
        }

        const requireHeatmapPdf = (make: () => KpiPdfExport | null, emptyMessage: string): KpiPdfExport => {
          const f = make();
          if (!f) throw new Error(emptyMessage);
          return f;
        };

        const line = 'Your PDF is ready to download.';

        switch (activeViewId) {
          case 'dashboard': {
            if (!chartsQuery.data) throw new Error('No dashboard data to export yet.');
            return {
              files: exportKpiDashboardMainPdf(chartsQuery.data, dateFrom, dateTo),
              summaryLines: [line],
            };
          }
          case 'heatmap-customer': {
            const hd = heatmapQuery.data;
            if (!hd) throw new Error('No heatmap data to export yet.');
            return {
              files: requireHeatmapPdf(
                () => exportKpiHeatmapCustomerViewPdf(hd, dateFrom, dateTo, 'Heatmap (Customer)'),
                'No matrix rows to export for the current filters.'
              ),
              summaryLines: [line],
            };
          }
          case 'heatmap-ci': {
            const hci = heatmapCiQuery.data;
            if (!hci) throw new Error('No heatmap data to export yet.');
            return {
              files: requireHeatmapPdf(
                () => exportKpiHeatmapCiViewPdf(hci.heatmap, dateFrom, dateTo),
                'No matrix rows to export for the current filters.'
              ),
              summaryLines: [line],
            };
          }
          case 'incident': {
            if (!chartsQuery.data || !heatmapQuery.data) {
              throw new Error('Load incident data before exporting.');
            }
            return {
              files: exportKpiIncidentTabPdf(chartsQuery.data, heatmapQuery.data, dateFrom, dateTo),
              summaryLines: [line],
            };
          }
          case 'request': {
            if (!chartsQuery.data || !heatmapQuery.data) {
              throw new Error('Load service request data before exporting.');
            }
            return {
              files: exportKpiServiceRequestTabPdf(chartsQuery.data, heatmapQuery.data, dateFrom, dateTo),
              summaryLines: [line],
            };
          }
          case 'case': {
            if (!chartsQuery.data) throw new Error('No case data to export yet.');
            return {
              files: exportKpiCaseTabPdf(chartsQuery.data, dateFrom, dateTo),
              summaryLines: [line],
            };
          }
          case 'change-request': {
            if (!chartsQuery.data) throw new Error('No change data to export yet.');
            return {
              files: exportKpiChangeRequestTabPdf(chartsQuery.data, dateFrom, dateTo),
              summaryLines: [line],
            };
          }
          case 'problems': {
            if (!chartsQuery.data) throw new Error('No problems data to export yet.');
            return {
              files: exportKpiProblemsTabPdf(chartsQuery.data, dateFrom, dateTo),
              summaryLines: [line],
            };
          }
          case 'resilience': {
            if (!chartsQuery.data) throw new Error('No resilience data to export yet.');
            return {
              files: exportKpiResiliencePdf(chartsQuery.data.resilience, dateFrom, dateTo),
              summaryLines: [line],
            };
          }
          case 'change-details':
            return {
              files: exportKpiFilterContextPdf('Change Details', dateFrom, dateTo, activeFilterChips),
              summaryLines: [line],
            };
          case 'all-tickets':
            return {
              files: exportKpiFilterContextPdf('All Tickets', dateFrom, dateTo, activeFilterChips),
              summaryLines: [line],
            };
          case 'sla-view':
            return {
              files: exportKpiFilterContextPdf('SLA View', dateFrom, dateTo, activeFilterChips),
              summaryLines: [line],
            };
          case 'msr-report': {
            if (!msrCustomerSelected) {
              throw new Error('Select a customer before exporting the MSR report.');
            }
            if (!msrReportQuery.data) {
              throw new Error('Load MSR report data before exporting.');
            }
            const msrPdf = await exportMsrReportPdf(msrReportQuery.data, dateFrom, dateTo);
            return {
              files: msrPdf,
              summaryLines: [line],
            };
          }
          default:
            throw new Error('PDF export is not available for this view.');
        }
      },
    });
  }, [
    run,
    drill,
    activeViewId,
    activeFilterChips,
    chartsQuery.data,
    heatmapQuery.data,
    heatmapCiQuery.data,
    dateFrom,
    dateTo,
    msrCustomerSelected,
    msrReportQuery.data,
  ]);

  const handleExportExcel = useCallback(() => {
    void run({
      downloadButtonLabel: 'Download Excel',
      work: async () => {
        if (activeViewId === 'msr-report') {
          if (!msrCustomerSelected) {
            throw new Error('Select a customer before exporting MSR raw data.');
          }
          const timezoneOffset = new Date().getTimezoneOffset();
          const raw = await fetchMsrReportRawData(filterValues, timezoneOffset);
          if (!raw.rows.length) {
            throw new Error('No tickets match the current customer and filters.');
          }
          const file = exportMsrRawDataExcel(raw.customerName, dateFrom, dateTo, raw.rows);
          return {
            files: file,
            summaryLines: [
              `Exported ${raw.rows.length.toLocaleString()} ticket row(s) to Excel.`,
            ],
          };
        }
        if (activeViewId !== 'dashboard') {
          throw new Error('Excel export is only available on the Dashboard and MSR Report tabs.');
        }
        const d = chartsQuery.data;
        if (!d) throw new Error('No dashboard data to export yet.');
        if (d.operationalAgeingByGroup.length === 0 && d.lastUpdatedByGroup.length === 0) {
          throw new Error('No open backlog summary rows match the current filters.');
        }
        const parts = [];
        if (d.operationalAgeingByGroup.length > 0) {
          parts.push(exportAgeingTableExcel(d.operationalAgeingByGroup));
        }
        if (d.lastUpdatedByGroup.length > 0) {
          parts.push(exportLastUpdatedTableExcel(d.lastUpdatedByGroup));
        }
        return {
          files: parts.length === 1 ? parts[0]! : parts,
          summaryLines: [
            parts.length > 1
              ? `${parts.length} Excel files are ready to download.`
              : 'Your Excel file is ready to download.',
          ],
        };
      },
    });
  }, [run, activeViewId, chartsQuery.data, msrCustomerSelected, filterValues, dateFrom, dateTo]);

  const showDrillOverlay =
    drill !== null &&
    (activeViewId === 'dashboard' ||
      activeViewId === 'heatmap-customer' ||
      activeViewId === 'heatmap-ci' ||
      activeViewId === 'incident' ||
      activeViewId === 'request' ||
      activeViewId === 'case' ||
      activeViewId === 'change-request' ||
      activeViewId === 'change-details' ||
      activeViewId === 'all-tickets' ||
      activeViewId === 'problems' ||
      activeViewId === 'resilience');

  return (
    <>
      {modal}
      <div className="flex h-full min-h-0 flex-col bg-gradient-to-b from-slate-100 via-slate-50 to-slate-100/80">
      {showDrillOverlay ? (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 pb-8 pt-4 sm:px-5">
          <div className="mx-auto flex min-h-0 w-full max-w-[1600px] flex-1 flex-col">
            <KpiDrilldownTicketsPanel
              drill={drill}
              filterValues={
                activeViewId === 'incident'
                  ? incidentScopedFilters
                  : activeViewId === 'request'
                    ? requestScopedFilters
                    : activeViewId === 'case'
                      ? caseScopedFilters
                      : activeViewId === 'change-request'
                        ? changeScopedFilters
                        : activeViewId === 'change-details'
                          ? changeDetailsScopedFilters
                          : activeViewId === 'all-tickets'
                            ? allTicketsScopedFilters
                            : activeViewId === 'problems'
                              ? problemScopedFilters
                              : filterValues
              }
              filterKey={
                activeViewId === 'incident'
                  ? incidentScopedFilterKey
                  : activeViewId === 'request'
                    ? requestScopedFilterKey
                    : activeViewId === 'case'
                      ? caseScopedFilterKey
                      : activeViewId === 'change-request'
                        ? changeScopedFilterKey
                        : activeViewId === 'change-details'
                          ? changeDetailsScopedFilterKey
                          : activeViewId === 'all-tickets'
                            ? allTicketsScopedFilterKey
                            : activeViewId === 'problems'
                              ? problemScopedFilterKey
                              : filterKey
              }
              resilienceCross={activeViewId === 'resilience' ? resilienceCrossFilter : null}
              onClose={() => setDrill(null)}
              backButtonLabel={
                activeViewId === 'heatmap-customer'
                  ? 'Back to heatmap'
                  : activeViewId === 'heatmap-ci'
                    ? 'Back to heatmap (CI)'
                    : activeViewId === 'incident'
                    ? 'Back to incident dashboard'
                    : activeViewId === 'request'
                      ? 'Back to service request dashboard'
                      : activeViewId === 'case'
                        ? 'Back to case dashboard'
                        : activeViewId === 'change-request'
                          ? 'Back to change request dashboard'
                          : activeViewId === 'change-details'
                            ? 'Back to change details'
                            : activeViewId === 'all-tickets'
                              ? 'Back to all tickets'
                              : activeViewId === 'problems'
                            ? 'Back to problems dashboard'
                            : activeViewId === 'resilience'
                              ? 'Back to resilience dashboard'
                              : 'Back to charts'
              }
            />
          </div>
        </div>
      ) : (
        <>
          <KpiDashboardFilterBar
            activeViewId={activeViewId}
            onViewChange={setActiveViewId}
            sections={filterSections}
            values={filterValues}
            onFilterChange={handleFilterChange}
            chips={activeFilterChips}
            groups={groups}
            assignmentGroupQuickValue={assignmentGroupQuickValue}
            onAssignmentGroupQuickChange={handleAssignmentGroupQuickChange}
            onRemoveChip={handleRemoveFilterChip}
            onClearAllFilters={handleClearAllFilters}
            showExport
            exportPdfDisabled={exportPdfDisabled}
            exportExcelDisabled={exportExcelDisabled}
            onExportPdf={handleExportPdf}
            onExportExcel={handleExportExcel}
            exportShowExcel={exportShowExcel}
          />

          <div
            className={
              activeViewId === 'change-details' ||
              activeViewId === 'all-tickets' ||
              activeViewId === 'sla-view'
                ? 'relative z-0 flex min-h-0 flex-1 flex-col overflow-hidden'
                : 'relative z-0 min-h-0 flex-1 overflow-auto px-4 pb-8 pt-2 sm:px-5'
            }
          >
            <div
              className={
                activeViewId === 'change-details' ||
                activeViewId === 'all-tickets' ||
                activeViewId === 'sla-view'
                  ? 'flex min-h-0 min-w-0 flex-1 flex-col'
                  : 'mx-auto max-w-[1600px]'
              }
            >
              {activeViewId === 'dashboard' ? (
                <KpiDashboardContent
                  data={chartsQuery.data}
                  isLoading={chartsQuery.isLoading}
                  isFetching={chartsQuery.isFetching}
                  error={chartsError}
                  onRetry={() => chartsQuery.refetch()}
                  onDrill={setDrill}
                  filterValues={filterValues}
                  chartsRenderRevision={chartsQuery.dataUpdatedAt}
                />
              ) : activeViewId === 'incident' ? (
                <KpiIncidentDashboardTab
                  chartsData={chartsQuery.data}
                  chartsLoading={chartsQuery.isLoading}
                  chartsFetching={chartsQuery.isFetching}
                  chartsError={chartsError}
                  chartsOnRetry={() => chartsQuery.refetch()}
                  heatmapData={heatmapQuery.data}
                  heatmapLoading={heatmapQuery.isLoading}
                  heatmapFetching={heatmapQuery.isFetching}
                  heatmapError={heatmapError}
                  heatmapOnRetry={() => heatmapQuery.refetch()}
                  onDrill={setDrill}
                />
              ) : activeViewId === 'request' ? (
                <KpiServiceRequestDashboardTab
                  chartsData={chartsQuery.data}
                  chartsLoading={chartsQuery.isLoading}
                  chartsFetching={chartsQuery.isFetching}
                  chartsError={chartsError}
                  chartsOnRetry={() => chartsQuery.refetch()}
                  heatmapData={heatmapQuery.data}
                  heatmapLoading={heatmapQuery.isLoading}
                  heatmapFetching={heatmapQuery.isFetching}
                  heatmapError={heatmapError}
                  heatmapOnRetry={() => heatmapQuery.refetch()}
                  onDrill={setDrill}
                />
              ) : activeViewId === 'case' ? (
                <KpiCaseDashboardTab
                  chartsData={chartsQuery.data}
                  chartsLoading={chartsQuery.isLoading}
                  chartsFetching={chartsQuery.isFetching}
                  chartsError={chartsError}
                  chartsOnRetry={() => chartsQuery.refetch()}
                  onDrill={setDrill}
                />
              ) : activeViewId === 'change-request' ? (
                <KpiChangeRequestDashboardTab
                  chartsData={chartsQuery.data}
                  chartsLoading={chartsQuery.isLoading}
                  chartsFetching={chartsQuery.isFetching}
                  chartsError={chartsError}
                  chartsOnRetry={() => chartsQuery.refetch()}
                  onDrill={setDrill}
                />
              ) : activeViewId === 'change-details' ? (
                <KpiChangeDetailsTab
                  filterValues={changeDetailsScopedFilters}
                  filterKey={changeDetailsScopedFilterKey}
                />
              ) : activeViewId === 'all-tickets' ? (
                <KpiAllTicketsTab
                  filterValues={allTicketsScopedFilters}
                  filterKey={allTicketsScopedFilterKey}
                />
              ) : activeViewId === 'sla-view' ? (
                <KpiSlaViewTab
                  filterValues={slaViewScopedFilters}
                  filterKey={slaViewScopedFilterKey}
                />
              ) : activeViewId === 'problems' ? (
                <KpiProblemDashboardTab
                  chartsData={chartsQuery.data}
                  chartsLoading={chartsQuery.isLoading}
                  chartsFetching={chartsQuery.isFetching}
                  chartsError={chartsError}
                  chartsOnRetry={() => chartsQuery.refetch()}
                  onDrill={setDrill}
                />
              ) : activeViewId === 'heatmap-customer' ? (
                <KpiHeatmapCustomerTab
                  data={heatmapQuery.data}
                  isLoading={heatmapQuery.isLoading}
                  isFetching={heatmapQuery.isFetching}
                  error={heatmapError}
                  onRetry={() => heatmapQuery.refetch()}
                  priorityFilter={
                    typeof filterValues.priority === 'string' ? filterValues.priority : 'All'
                  }
                  onDrill={setDrill}
                />
              ) : activeViewId === 'heatmap-ci' ? (
                <KpiHeatmapCiTab
                  data={heatmapCiQuery.data}
                  isLoading={heatmapCiQuery.isLoading}
                  isFetching={heatmapCiQuery.isFetching}
                  error={heatmapCiError}
                  onRetry={() => heatmapCiQuery.refetch()}
                  onDrill={setDrill}
                  focus={heatmapCiFocus}
                  onFocusChange={setHeatmapCiFocus}
                />
              ) : activeViewId === 'resilience' ? (
                <KpiResilienceDashboardTab
                  chartsData={chartsQuery.data}
                  chartsLoading={chartsQuery.isLoading}
                  chartsFetching={chartsQuery.isFetching}
                  chartsError={chartsError}
                  chartsOnRetry={() => chartsQuery.refetch()}
                  onDrill={setDrill}
                  crossFilter={resilienceCrossFilter}
                  onCrossFilterChange={setResilienceCrossFilter}
                />
              ) : activeViewId === 'msr-report' ? (
                <KpiMsrReportTab
                  data={msrReportQuery.data}
                  isLoading={msrReportQuery.isLoading}
                  isFetching={msrReportQuery.isFetching}
                  error={msrReportError}
                  onRetry={() => msrReportQuery.refetch()}
                  customerSelected={msrCustomerSelected}
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                  filterValues={filterValues}
                  azureCostCustomerId={(filterValues.customer as string) || ''}
                  azureCostCustomerName={selectedCustomerName}
                  azureCostSubscription={{
                    data: azureCostSections.subscription.data,
                    isLoading: azureCostSections.subscription.isLoading,
                    isFetching: azureCostSections.subscription.isFetching,
                    error: toQueryError(azureCostSections.subscription.error),
                    onRetry: () => void azureCostSections.subscription.refetch(),
                  }}
                  azureCostLast3MonthsAnalysis={{
                    data: azureCostSections.last3MonthsAnalysis.data,
                    isLoading: azureCostSections.last3MonthsAnalysis.isLoading,
                    isFetching: azureCostSections.last3MonthsAnalysis.isFetching,
                    error: toQueryError(azureCostSections.last3MonthsAnalysis.error),
                    onRetry: () => void azureCostSections.last3MonthsAnalysis.refetch(),
                  }}
                />
              ) : (
                <div className="flex min-h-[420px] flex-col items-center justify-center rounded-2xl border border-slate-200/90 bg-gradient-to-br from-white via-slate-50/50 to-indigo-50/20 p-12 text-center shadow-lg shadow-slate-900/5 ring-1 ring-slate-950/[0.04]">
                  <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-100 to-violet-100 shadow-inner">
                    <ChartBarIcon className="h-10 w-10 text-indigo-500" aria-hidden />
                  </div>
                  <h2 className="mt-6 text-lg font-semibold tracking-tight text-slate-800">{activeViewLabel}</h2>
                  <p className="mt-2 max-w-sm text-sm leading-relaxed text-slate-500">
                    This view is available when selected from the dashboard menu.
                  </p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
    </>
  );
};

export default KpiDashboard;
